import { AdminConversation } from '../../services/admin/FidesChatAdminService';

export const FIDES_CHAT_TRAY_EDGE_OFFSET = 24;
export const FIDES_CHAT_TRAY_RIGHT_OFFSET = 88;
export const FIDES_CHAT_TRAY_WIDTH = 360;
export const FIDES_CHAT_TRAY_HEIGHT = 480;
export const FIDES_CHAT_BUBBLE_MAX_WIDTH = 280;
export const FIDES_CHAT_BUBBLE_DISMISS_MS = 8000;
export const FIDES_CHAT_BUBBLE_RECENCY_MS = 5 * 60 * 1000;

export interface BubbleNotification {
  conversationId: string;
  name: string;
  text: string;
}

export function getDisplayName(conversation: AdminConversation): string {
  return conversation.contact?.name?.trim() || conversation.contact?.email?.trim() || 'Investor';
}

export function getInitials(name: string): string {
  const parts = name.split(' ').filter(Boolean).slice(0, 2);
  if (parts.length === 0) {
    return 'I';
  }

  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('');
}

export function adminExternalId(admin: {
  externalId?: string | null;
  id?: string | number | null;
}): string | null {
  if (admin.externalId?.trim()) return admin.externalId.trim();
  if (admin.id != null) return String(admin.id);
  return null;
}

export function formatConversationTime(value: string | null): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const isToday = new Date().toDateString() === date.toDateString();

  return new Intl.DateTimeFormat(
    [],
    isToday ? { hour: 'numeric', minute: '2-digit' } : { month: 'short', day: 'numeric' },
  ).format(date);
}

export function buildBubbleDedupeKey(conversation: AdminConversation): string {
  return `${conversation.conversationId}:${conversation.lastMessageAt ?? conversation.lastMessageText}`;
}

export function isRecentMessage(lastMessageAt: string | null, now: number): boolean {
  if (!lastMessageAt) {
    return false;
  }

  return now - new Date(lastMessageAt).getTime() <= FIDES_CHAT_BUBBLE_RECENCY_MS;
}

export function shouldShowConversationBubble(
  conversation: AdminConversation,
  previous: AdminConversation | undefined,
  options: {
    open: boolean;
    isPanelOpen: boolean;
    openConversationId?: string | null;
    now: number;
    shownKeys: Set<string>;
  },
): boolean {
  if (!conversation.unreadByAdmin) {
    return false;
  }

  const isNewConversation = !previous;
  const isNewMessage =
    Boolean(previous) &&
    previous!.lastMessageText !== conversation.lastMessageText &&
    Boolean(conversation.lastMessageText);

  if (!isNewConversation && !isNewMessage) {
    return false;
  }

  if (
    options.open ||
    (options.isPanelOpen && options.openConversationId === conversation.conversationId)
  ) {
    return false;
  }

  if (!isRecentMessage(conversation.lastMessageAt, options.now)) {
    return false;
  }

  const key = buildBubbleDedupeKey(conversation);
  if (options.shownKeys.has(key)) {
    return false;
  }

  return true;
}
